import React, { useState } from 'react';
import { 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  MagnifyingGlassIcon,
  TagIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const ImageGallery = ({ 
  images = [], 
  onImageUpdate, 
  onImageDelete, 
  onImagePreview,
  editable = true,
  className = '',
  gridCols = 'grid-cols-1 md:grid-cols-1 lg:grid-cols-2'
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTag, setFilterTag] = useState('');

  // Tüm etiketleri topla
  const allTags = [...new Set(images.flatMap(img => img.tags || []))];

  // Filtrelenmiş görseller
  const filteredImages = images.filter(image => {
    const matchesSearch = image.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         image.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTag = !filterTag || (image.tags && image.tags.includes(filterTag));
    
    return matchesSearch && matchesTag;
  });

  // Görsel silme handler'ı
  const handleDelete = (imageId, e) => {
    e.stopPropagation();
    if (window.confirm('Bu görseli silmek istediğinizden emin misiniz?')) {
      onImageDelete(imageId);
    }
  };

  // Görsel düzenleme handler'ı
  const handleEdit = (image, e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setSelectedImage(image);
  };

  // Görsel önizleme handler'ı
  const handlePreview = (image, e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    if (onImagePreview) {
      onImagePreview(image);
    }
  };

  // Dosya boyutu formatı
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Tarih formatı
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (images.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <p className="text-gray-500 dark:text-gray-400">Henüz görsel yüklenmemiş</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header ve Filtreler */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Görsel Galeri
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {filteredImages.length} / {images.length} görsel
          </p>
        </div>

        {/* Arama ve Filtre */}
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          {/* Arama */}
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Görsel ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Etiket Filtresi */}
          {allTags.length > 0 && (
            <select
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Tüm etiketler</option>
              {allTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Görsel Grid */}
      <div className={`grid ${gridCols} gap-4`}>
        {filteredImages.map((image) => (
          <div
            key={image.id}
            className="group relative bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-all duration-200 cursor-pointer"
            onClick={(e) => handlePreview(image, e)}
          >
            {/* Görsel */}
            <div className="bg-gray-100 dark:bg-gray-700 p-4">
              <img
                src={image.preview || image.file_path || image.optimized_url || image.thumbnail_url}
                alt={image.name}
                className="w-full h-auto object-contain"
                style={{ maxHeight: '500px', minHeight: '200px' }}
                loading="lazy"
              />
            </div>

            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 flex space-x-2">
                {/* Önizleme */}
                <button
                  onClick={(e) => handlePreview(image, e)}
                  className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                  title="Önizle"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>

                {/* Düzenleme */}
                {editable && (
                  <button
                    onClick={(e) => handleEdit(image, e)}
                    className="p-2 bg-blue-500 rounded-full text-white hover:bg-blue-600 transition-colors"
                    title="Düzenle"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                )}

                {/* Silme */}
                {editable && (
                  <button
                    onClick={(e) => handleDelete(image.id, e)}
                    className="p-2 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
                    title="Sil"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Görsel Bilgileri */}
            <div className="p-3">
              <div className="space-y-2">
                {/* Dosya Adı */}
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={image.name}>
                  {image.name}
                </p>

                {/* Açıklama */}
                {image.description && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                    {image.description}
                  </p>
                )}

                {/* Etiketler */}
                {image.tags && image.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {image.tags.slice(0, 2).map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                      >
                        <TagIcon className="w-3 h-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                    {image.tags.length > 2 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        +{image.tags.length - 2}
                      </span>
                    )}
                  </div>
                )}

                {/* Metadata */}
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>{formatFileSize(image.size)}</span>
                  {image.uploadedAt && (
                    <div className="flex items-center space-x-1">
                      <CalendarIcon className="w-3 h-3" />
                      <span>{formatDate(image.uploadedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Sonuç Bulunamadı */}
      {filteredImages.length === 0 && images.length > 0 && (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-2">
            <MagnifyingGlassIcon className="w-12 h-12 mx-auto" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">
            Arama kriterlerinize uygun görsel bulunamadı
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterTag('');
            }}
            className="mt-2 text-sm text-blue-600 hover:text-blue-700"
          >
            Filtreleri temizle
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
